import type { QueryTypes } from '@medplum/core';
import type { ResourceType } from '@medplum/fhirtypes';

type SearchFunction = 'search' | 'searchOne' | 'searchResources';

/**
 * Query key structure for Medplum search queries.
 * Format: ['medplum', resourceType, searchFn, query]
 *
 * This structure allows for:
 * - Invalidating all Medplum queries: ['medplum']
 * - Invalidating all queries for a resource type: ['medplum', 'Patient']
 * - Invalidating specific search types: ['medplum', 'Patient', 'search']
 * - Invalidating specific queries: ['medplum', 'Patient', 'search', { name: 'John' }]
 */
export type MedplumSearchQueryKey = readonly ['medplum', ResourceType, SearchFunction, QueryTypes | undefined];

/**
 * Creates a query key for Medplum search operations.
 *
 * @param resourceType - The FHIR resource type being searched.
 * @param query - Optional search query parameters.
 * @param searchFn - The search function type ('search', 'searchOne', or 'searchResources').
 * @returns A structured query key for use with TanStack Query.
 *
 * @example
 * ```ts
 * const queryKey = createSearchQueryKey('Patient', { name: 'John' }, 'search');
 * // ['medplum', 'Patient', 'search', { name: 'John' }]
 * ```
 */
export function createSearchQueryKey(
  resourceType: ResourceType,
  query: QueryTypes | undefined,
  searchFunction: SearchFunction,
): MedplumSearchQueryKey {
  return ['medplum', resourceType, searchFunction, query] as const;
}

/**
 * Query key factory for Medplum operations.
 * Use these to create query keys for use with TanStack Query's invalidation and prefetching.
 */
export const medplumQueryKeys = {
  /**
   * Base key for all Medplum queries.
   * Use this to invalidate all Medplum-related queries.
   *
   * @example
   * ```ts
   * queryClient.invalidateQueries({ queryKey: medplumQueryKeys.all });
   * ```
   */
  all: ['medplum'] as const,

  /**
   * Key for all queries of a specific resource type.
   *
   * @param resourceType - The FHIR resource type.
   * @returns Query key array.
   *
   * @example
   * ```ts
   * // Invalidate all Patient queries
   * queryClient.invalidateQueries({ queryKey: medplumQueryKeys.resource('Patient') });
   * ```
   */
  resource: (resourceType: ResourceType) => ['medplum', resourceType] as const,

  /**
   * Key for all search queries of a specific resource type.
   *
   * @param resourceType - The FHIR resource type.
   * @returns Query key array.
   *
   * @example
   * ```ts
   * // Invalidate all Patient search queries
   * queryClient.invalidateQueries({ queryKey: medplumQueryKeys.searches('Patient') });
   * ```
   */
  searches: (resourceType: ResourceType) => ['medplum', resourceType, 'search'] as const,

  /**
   * Key for a specific search query.
   *
   * @param resourceType - The FHIR resource type.
   * @param query - The search query parameters.
   * @returns Query key array.
   *
   * @example
   * ```ts
   * // Invalidate specific Patient search
   * queryClient.invalidateQueries({
   *   queryKey: medplumQueryKeys.search('Patient', { name: 'John' })
   * });
   * ```
   */
  search: (resourceType: ResourceType, query?: QueryTypes) =>
    ['medplum', resourceType, 'search', query] as const,

  /**
   * Key for all searchOne queries of a specific resource type.
   *
   * @param resourceType - The FHIR resource type.
   * @returns Query key array.
   */
  searchOnes: (resourceType: ResourceType) => ['medplum', resourceType, 'searchOne'] as const,

  /**
   * Key for a specific searchOne query.
   *
   * @param resourceType - The FHIR resource type.
   * @param query - The search query parameters.
   * @returns Query key array.
   */
  searchOne: (resourceType: ResourceType, query?: QueryTypes) =>
    ['medplum', resourceType, 'searchOne', query] as const,

  /**
   * Key for all searchResources queries of a specific resource type.
   *
   * @param resourceType - The FHIR resource type.
   * @returns Query key array.
   */
  searchResourcesQueries: (resourceType: ResourceType) => ['medplum', resourceType, 'searchResources'] as const,

  /**
   * Key for a specific searchResources query.
   *
   * @param resourceType - The FHIR resource type.
   * @param query - The search query parameters.
   * @returns Query key array.
   */
  searchResourcesQuery: (resourceType: ResourceType, query?: QueryTypes) =>
    ['medplum', resourceType, 'searchResources', query] as const,
} as const;
